import React, {
    useContext,
    useEffect,
    useState,
    useRef,
    useMemo,
} from 'react';
import './astute-topic.scss';
import { SUPPORT_HUB_REGEX } from '../../support-constants';
import { ExternalSiteNotableUtil } from '../../../components/utils/external-site-notable-util/external-site-notable-util';
import { useAnalytics } from '../../../hooks/use-analytics';
import {
    DiscoverabilityPageLoad,
    ShortcodeProviders,
} from '../../../services/shortcode-service/shortcode-service';
import ServerContext from '../../../contexts/serverContext';
import { useAppInformationContent } from '../../../components/sections/breadcrumbs/hook/use-breadcrumb-content';
import { useBbtContent } from '../browse-by-topic/hook/use-bbt-content';
import { useParams } from 'react-router';
import AstuteService, {
    serializeFaqSchemaJson,
} from '../../services/astute-service/astute-service';
import { useServerData } from '../../../hooks/use-server-data';
import siteMapService from '../../services/site-map-service/site-map-service';
import DisclosuresLinkSyndicationAware from '../../../components/sections/disclosures-link/disclosures-link-syndication-aware';
import { TopicPage } from '../../models/topic-page';
import { useSupportCommonContent } from './hook/use-support-common';
import serverSideService from '../../../services/server-side-service/server-side-service';
import printIcon from '../../../assets/print-icon.svg';
import { accordionQuery } from '../astute-accordion/astute-accordion';
import BreadcrumbsAndViewBanner from '../breadcrumbs-and-view-banner/breadcrumbs-and-view-banner';
import WebFlashMessage from '../../../components/common/web-flash-message/web-flash-message';
import {
    enableLanguageLinksforCA,
    enableLanguageLinksforTH,
} from '../../../components/utils/syndicate-util/syndicate-util';
import useUserProfile from '../../../hooks/use-user-profile';
import {
    getCategoryNameFromTopicResponse,
    buildBreadcrumbListSchema,
    getCurrentLanguageRegionCodeFromServerContext,
    getCurrentRootFromServerContext,
    getDocumentTitleFromTopicPage,
    getSubCategoryNameFromTopicResponse,
    getTopicLabelFromTopicResponse,
    loadScriptsForAstuteTopicData,
    processSearchParam,
} from './util/astute-topic-util';
import AstuteTopicTeslaCharger from '../astute-topic-teslacharger/astute-topic-tesla-charger';
import AstuteTopicEmplifiFeedback from '../astute-topic-emplifi-feedback/astute-topic-emplifi-feedback';
import { useCanonicalTagContent } from './hook/use-canonical-tag-content';
import { AstuteTopicPageBreadcrumb } from './astute-topic-page-breadcrumb';

interface TopicUrlParams {
    category: string;
    subcategory: string;
    topic: string;
}

export const printButton = (printAriaLabel: string | undefined) => (
    <div className="print-button-container">
        <button
            className="print-button"
            aria-label={printAriaLabel || 'click to print article'}
            onClick={() => {
                printerPage();
            }}
        >
            <img src={printIcon} alt="" />
        </button>
    </div>
);
export const printerPage = () => {
    serverSideService.isClientSide() && window.print();
};
const astuteService = new AstuteService();
export const onAnchorClick = (event: Event) => {
    const externalSiteNotableUtil = new ExternalSiteNotableUtil();

    const anchorEle = externalSiteNotableUtil.getParentByTag(event.target, 'A');
    setTimeout(() => {
        const element = document.getElementById(
            (anchorEle as HTMLAnchorElement)?.href.split('#')[1]
        );
        element && window.scrollTo(element.offsetLeft, element.offsetTop - 140);
    });
};
export const fireOwnerArticleEvent = (
    languageCode: string,
    categoryName: string,
    subCategoryName: string,
    topicLabel: string,
    fireEvents: (
        eventNames: string | string[],
        targetViewName?: string,
        shortcodeProviders?: ShortcodeProviders,
        clearDigitaldata?: boolean,
        skipFireTargetAnalytics?: boolean
    ) => void,
    slugifiedSearchTerm?: string
) => {
    const topicLabelForAnalytics = astuteService.sanitizeString(
        topicLabel,
        languageCode
    );
    const discoverabilityPageLoad: DiscoverabilityPageLoad = {
        category: categoryName?.replaceAll('-', ' '),
        subcategory: subCategoryName?.replaceAll('-', ' '),
        article: topicLabelForAnalytics?.replaceAll('-', ' '),
        searchKeyword: slugifiedSearchTerm,
    };
    !slugifiedSearchTerm && delete discoverabilityPageLoad.searchKeyword;
    fireEvents(
        ['owner-article'],
        undefined,
        { discoverabilityPageLoad },
        false
    );
};

const AstuteTopic = () => {
    const { category, subcategory, topic } = useParams<TopicUrlParams>();
    const serverContext = useContext(ServerContext);
    const bbtContent = useBbtContent();
    const homePagetitle = useAppInformationContent();
    const [accordions, setAccordions] = useState<Element[] | null>();
    const [astuteTopicDiv, setAstuteTopicDiv] = useState<Element | null>();
    const supportCommonContent = useSupportCommonContent();
    const canonicalTagContent = useCanonicalTagContent();
    const [fireEvents] = useAnalytics();
    const profile = useUserProfile();
    const languageCode = getCurrentLanguageRegionCodeFromServerContext(
        serverContext
    );
    const currentRoot = getCurrentRootFromServerContext(serverContext);

    const baseUrl = `https://${serverContext.domain}${currentRoot}`;

    const isLincoln = serverContext.brand === 'lincoln';
    const fetchingRef = useRef<Promise<TopicPage> | null>(null);

    const astuteTopicPage = async () => {
        if (fetchingRef.current) {
            return fetchingRef.current;
        }

        let topicPage: TopicPage = {
            topicId: 0,
            metaDescription: '',
            hrefLangs: '',
            sessionId: '',
        };
        const fetchPromise = (async () => {
            try {
                topicPage = await astuteService.getTopicById(
                    category,
                    subcategory,
                    topic,
                    serverContext.currentLanguageRegionCode,
                    serverContext.domain,
                    serverContext.root,
                    serverContext.brand
                );
                serverContext.documentTitle = getDocumentTitleFromTopicPage(
                    topicPage
                );
                serverContext.metaDescription = topicPage.metaDescription;
                serverContext.hrefLangs = topicPage.hrefLangs;
                serverContext.noIndex = topicPage.noIndex;
                return topicPage;
            } catch (error) {
                topicPage.error = `Topic Page Error: ${error}`;
                return topicPage;
            } finally {
                fetchingRef.current = null;
            }
        })();

        fetchingRef.current = fetchPromise;
        return fetchPromise;
    };

    const topicResponse = useServerData('astuteTopic', astuteTopicPage)[0];

    const categoryPath = siteMapService.getPathFromRouterConfigs(
        serverContext.currentLanguageRegionCode,
        serverContext.brand,
        'CategoryPageView',
        [category]
    );
    const subCategoryPath = siteMapService.getPathFromRouterConfigs(
        serverContext.currentLanguageRegionCode,
        serverContext.brand,
        'SubCategoryPageView',
        [category, subcategory]
    );
    
    const schemaScripts = useMemo(() => {
        const topicHeadingForScript = topicResponse?.data?.topicHeading
            ?.replace(SUPPORT_HUB_REGEX.ALL_MARKUP_TAGS, '')
            .replace(SUPPORT_HUB_REGEX.FIND_ALL_TABS_NEW_LINE, '')
            .replace(SUPPORT_HUB_REGEX.NON_BREAKING_SPACE, ' ')
            .replace(SUPPORT_HUB_REGEX.SPACES, ' ')
            .replace(SUPPORT_HUB_REGEX.DOUBLE_QUOTES, '');

        const breadcrumbScript =
            topicResponse?.data &&
            bbtContent &&
            !bbtContent?.hideBrowseByTopic &&
            buildBreadcrumbListSchema([
                { id: baseUrl, name: homePagetitle?.applicationName },
                {
                    id: `${baseUrl}${categoryPath}`,
                    name: topicResponse?.data?.categoryName,
                },
                {
                    id: `${baseUrl}${subCategoryPath}`,
                    name: topicResponse?.data?.subCategoryName,
                },
                {
                    id: topicResponse?.data?.canonical,
                    name: topicHeadingForScript,
                },
            ]);

        return breadcrumbScript || '';
    }, [
        topicResponse?.data,
        bbtContent,
        homePagetitle,
        baseUrl,
        categoryPath,
        subCategoryPath,
    ]);

    const faqSchemaJson = topicResponse?.data?.faqSchema
        ? serializeFaqSchemaJson(topicResponse.data.faqSchema)
        : '';

    const fireAdobeAnalytics = () => {
        const categoryName = getCategoryNameFromTopicResponse(topicResponse);
        const subCategoryName = getSubCategoryNameFromTopicResponse(
            topicResponse
        );
        const topicLabel = getTopicLabelFromTopicResponse(topicResponse);
        const searchNonSearch = 'non-searched';
        fireEvents(
            ['search-nonsearch-click'],
            undefined,
            {
                searchNonSearch,
            },
            false
        );
        fireOwnerArticleEvent(
            languageCode,
            categoryName,
            subCategoryName,
            topicLabel,
            fireEvents
        );
    };

    if (canonicalTagContent?.canonicalTagTopicIds) {
        const matchingCanonicalTag = canonicalTagContent.canonicalTagTopicIds.find(
            canonicalTag =>
                canonicalTag.topicId === topicResponse?.data?.topicId.toString()
        );
        if (matchingCanonicalTag) {
            serverContext.canonical = matchingCanonicalTag.canonicalTag;
        }
    }

    useEffect(() => {
        processSearchParam();
    }, []);

    useEffect(() => {
        loadScriptsForAstuteTopicData({
            topicResponse,
            category,
            subcategory,
            currentRoot,
            subCategoryPath,
            bbtContent,
            serverContext,
            fireAdobeAnalytics,
        });
        return () => {
            document.removeEventListener('click', onAnchorClick, false);
        };
    }, [topicResponse?.data]);

    useEffect(() => {
        enableLanguageLinksforTH();
        enableLanguageLinksforCA();
        setAccordions(accordionQuery());

        const astuteTopicDiv = document.querySelector('.astute-response');
        setAstuteTopicDiv(astuteTopicDiv);
    }, [topicResponse, profile, accordions?.length]);

    return topicResponse?.data?.topicLabel ? (
        <div className="astute-topic">
            <WebFlashMessage
                experience={'emplifi'}
                fragmentName={'experience-notification-banner'}
            />
            {bbtContent && !bbtContent?.hideBrowseByTopic && (
                <BreadcrumbsAndViewBanner
                    currentPage={topicResponse.data.topicLabel || ''}
                    hashId={'#bbt'}
                    previousCrumb={
                        <AstuteTopicPageBreadcrumb
                            topicResponse={topicResponse}
                            categoryPath={categoryPath}
                            subCategoryPath={subCategoryPath}
                            currentRoot={currentRoot}
                        />
                    }
                    searchClassName="sh-search"
                />
            )}
            <div className="astute-topic-content">
                {printButton(supportCommonContent?.printAriaLabel)}
                <div
                    className={`${
                        isLincoln ? 'astute-topic-content-wrapper' : ''
                    }`}
                >
                    <div
                        dangerouslySetInnerHTML={{
                            __html: schemaScripts.concat(
                                topicResponse?.data?.topicHeading?.replace(
                                    /&nbsp;/g,
                                    ' '
                                ) || ''
                            ),
                        }}
                    />

                    {faqSchemaJson && (
                        <script
                            type="application/ld+json"
                            data-testid="faq-schema-script"
                            dangerouslySetInnerHTML={{ __html: faqSchemaJson }}
                        />
                    )}

                    <AstuteTopicTeslaCharger
                        astuteTopicDiv={astuteTopicDiv}
                        accordions={accordions}
                        topicResponse={topicResponse}
                    />

                    <div
                        className={`astute-response ${
                            accordions && accordions.length > 0
                                ? 'replace-accordion'
                                : ''
                        }`}
                        dangerouslySetInnerHTML={{
                            __html: '' + topicResponse.data?.bodyWoHeading,
                        }}
                    />

                    <AstuteTopicEmplifiFeedback topicResponse={topicResponse} />

                    {topicResponse?.data?.relatedTopics && (
                        <div className="article-related-topics">
                            <h2>{supportCommonContent?.relatedTopics}</h2>

                            <div
                                dangerouslySetInnerHTML={{
                                    __html: topicResponse.data?.relatedTopics,
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
            <DisclosuresLinkSyndicationAware />
        </div>
    ) : (
        <></>
    );
};
export default AstuteTopic;
